import { ProductBuilderOptions, ProductBuilderSelection } from "../types/ProductBuilderTypes";
import { MpvProvider } from "./MpvProvider";
import { getCompatibilityAndQuantities } from "../services/compatibility";
import { MerchandisingDetailProperty, MerchandisingProductView } from "../types/ContentfulTypes";
import {
  getNoPreselectedOptionsFromUrl,
  getQuantityFromUrl,
  getQueryParametersFromUrl,
} from "./urlHelper";
import { transformKeyAndValueToLowerCase } from "./transformObject";
import { getProductCompletion } from "../services/ranch";
import RangeType from "../types/RangeTypes";
import { isInRanges } from "./isInRanges";

export const getDefaultSelections = async ({
  productBuilderOptions,
  country,
  mpvProvider,
}: {
  productBuilderOptions: ProductBuilderOptions;
  country: string;
  mpvProvider: MpvProvider;
}) => {
  const preselectedOptions = mpvProvider.getPreselectedOptions(productBuilderOptions);
  if (Object.keys(preselectedOptions).length > 0) {
    const fullMpv = mpvProvider.getMpv();

    // We're calling Product Completion to check if the preselected options are compatible / not constrained
    const completeProductConfiguration = await checkProductCompletion({
      attrs: preselectedOptions,
      productKey: fullMpv.coreProductId,
      market: country.toLowerCase(),
      version: fullMpv.version,
    });

    if (completeProductConfiguration) {
      return preselectedOptions;
    }
  }

  return {};
};

export const getPreselectedOptionsFromUrl = (options: {
  mpv: MerchandisingProductView;
  merchandisingDetailProperties: MerchandisingDetailProperty[];
}): Record<string, string> => {
  try {
    const { mpv, merchandisingDetailProperties } = options;
    const usePreselectedOptions = !getNoPreselectedOptionsFromUrl();
    if (!usePreselectedOptions) {
      return {};
    }

    const queryParameters = getQueryParametersFromUrl();
    const mappedSelectionsFromQuery = validateSelectionsWithMpv({
      selections: queryParameters,
      mpv,
      merchandisingDetailProperties,
    });

    // Merge url selections with default selections
    const selections = {
      ...mappedSelectionsFromQuery,
    };

    return selections;
  } catch {
    return {};
  }
};

const validateSelectionsWithMpv = (params: {
  selections: Record<string, string>;
  mpv: MerchandisingProductView;
  merchandisingDetailProperties: MerchandisingDetailProperty[];
}) => {
  const { selections, mpv, merchandisingDetailProperties } = params;
  const mpvProvider = new MpvProvider(mpv);
  const productPageOptions = mpvProvider.getOptionKeys();
  const selectionsFromUrl = validateUrlPreselections(mpvProvider.getOptionKeys(), selections);
  const qtyFromUrl = getQuantityFromUrl();
  if (!(Object.keys(selectionsFromUrl).length > 0 || qtyFromUrl)) {
    return undefined;
  }
  const queryParams = transformKeyAndValueToLowerCase(selectionsFromUrl);
  let haveInvalidUrlSelections = false;
  const mappedSelectionsFromQuery = merchandisingDetailProperties
    .filter(({ key }) => productPageOptions.includes(key) && queryParams[key.toLowerCase()])
    .reduce((options: Record<string, string>, properties: MerchandisingDetailProperty) => {
      const option = (properties.options || []).find(
        ({ key }) => key.toLowerCase() === queryParams[properties.key.toLowerCase()].toLowerCase()
      );
      if (option) {
        options[properties.key] = option.key;
        return options;
      }

      if ((properties.ranges || []).length > 0) {
        if (isInRanges(queryParams[properties.key.toLowerCase()], properties.ranges!)) {
          options[properties.key] = queryParams[properties.key.toLowerCase()];
          return options;
        }
      }

      haveInvalidUrlSelections = true;
      return options;
    }, {});

  if (haveInvalidUrlSelections) {
    return {};
  }

  return mappedSelectionsFromQuery;
};

const validateSelectionCompatibility = async (params: {
  selections: Record<string, string>;
  country: string;
  mpv: MerchandisingProductView;
  productBuilderSelections: ProductBuilderSelection[];
  useCache: boolean;
}) => {
  const { selections, country, mpv, productBuilderSelections, useCache } = params;
  const mpvProvider = new MpvProvider(mpv);
  const fullMpv = mpvProvider.getMpv();
  const productPageOptions = mpvProvider.getOptionKeys();
  const qty = getQuantityFromUrl();

  // Use Product Completion to check if the preselected options are compatible / not constrained
  const completePossibleConfiguration = await checkProductCompletion({
    productKey: fullMpv.coreProductId,
    attrs: selections,
    market: country.toLowerCase(),
    version: fullMpv.version,
  });
  if (!completePossibleConfiguration) {
    return {};
  }

  const [
    compatibleValues,
    compatibleRanges,
    compatibleQuantities,
  ] = await getCompatibilityAndQuantities({
    coreProductId: fullMpv.coreProductId,
    selections,
    currentCompatibleRanges: {},
    currentCompatibleValues: {},
    currentCompatibleQuantities: [],
    productPageOptions,
    useCache,
  });

  return compatibleQuantities.length &&
    (qty === undefined || compatibleQuantities.includes(qty)) &&
    isPreselectedOptionsFromURLCompatible(
      productBuilderSelections,
      compatibleValues,
      compatibleRanges,
      selections
    )
    ? selections
    : {};
};

const isPreselectedOptionsFromURLCompatible = (
  productBuilderSelections: ProductBuilderSelection[],
  compatibleValues: Record<string, string[]>,
  compatibleRanges: Record<string, RangeType[]>,
  selection: Record<string, string>
): boolean => {
  return productBuilderSelections
    .filter(builderSelection => builderSelection.property)
    .every(({ property: { key } }) => {
      return (
        (Array.isArray(compatibleValues[key]) &&
          (selection[key] === undefined || compatibleValues[key].includes(selection[key]))) ||
        (Array.isArray(compatibleRanges[key]) && isInRanges(selection[key], compatibleRanges[key]))
      );
    });
};

const validateUrlPreselections = (optionKeys: string[], params: Record<string, string>) => {
  const lowerCaseKeys = optionKeys.map(k => k.toLowerCase());
  const filteredParams = {
    ...params,
  };
  Object.keys(filteredParams)
    .filter(optionKey => !lowerCaseKeys.includes(optionKey.toLowerCase()))
    .forEach(optionKey => delete filteredParams[optionKey]);

  return filteredParams;
};

const checkProductCompletion = async (props: {
  productKey: string;
  attrs: Record<string, string>;
  market: string;
  version: number;
}) => {
  const { productKey, attrs, market, version } = props;

  const productCompletionRequest = {
    selectedOptions: attrs,
    productId: productKey,
    market,
    version,
  };

  return await getProductCompletion(productCompletionRequest);
};

export const getSelections = async (props: {
  productBuilderOptions: ProductBuilderOptions;
  mpv: MerchandisingProductView;
  country: string;
}) => {
  const { productBuilderOptions, mpv, country } = props;
  const mpvProvider = new MpvProvider(mpv);
  const productPageMerchDetails = mpvProvider.getProductPageMerchandisingDetails();

  const onPageBuilderSelections = productBuilderOptions?.selections || [];
  const defaultSelectionsFromUrl = getPreselectedOptionsFromUrl({
    mpv,
    merchandisingDetailProperties: productPageMerchDetails?.properties || [],
  });

  if (Object.keys(defaultSelectionsFromUrl).length > 0) {
    const validatedSelectionsFromUrl = await validateSelectionCompatibility({
      selections: defaultSelectionsFromUrl ?? {},
      mpv,
      useCache: true,
      productBuilderSelections: onPageBuilderSelections,
      country,
    });
    // Use only the url selections if they are valid
    if (Object.keys(validatedSelectionsFromUrl).length > 0) {
      return validatedSelectionsFromUrl;
    }
  }

  const useMpvPreselectedOptions = !getNoPreselectedOptionsFromUrl();
  if (!useMpvPreselectedOptions) {
    return {};
  }

  const defaultSelections = await getDefaultSelections({
    productBuilderOptions,
    mpvProvider,
    country,
  });

  return defaultSelections;
};
